import React, { useCallback, useRef, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Download, Play, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import GatedDownloadModal from '../components/GatedDownloadModal';
import VideoModal from '../components/VideoModal';
import SEO, { articleJsonLd, breadcrumbJsonLd } from '../components/SEO';
import HeroMediaLayer from '../components/HeroMediaLayer';
import { HeroMuteToggle, useIsDesktop, useAutoMuteOnScroll } from '../components/HeroMuteToggle';
import useCases, { useCaseBySlug } from '../data/useCases';
import { trackEvent } from '../lib/analytics';

const UseCasePage = ({ onRequestDemo, onRequestQuote }) => {
  const { slug } = useParams();
  const useCase = useCaseBySlug(slug);
  const [gatedAsset, setGatedAsset] = useState(null);
  const [videoAsset, setVideoAsset] = useState(null);
  const [muted, setMuted] = useState(true);
  const isDesktop = useIsDesktop();
  const desktopMedia = useRef(null);
  const mobileMedia = useRef(null);
  const sectionRef = useRef(null);
  const fadeMute = useCallback(() => {
    setMuted(true);
    const m = (isDesktop ? desktopMedia : mobileMedia).current;
    if (m) m.fadeToMute(700);
  }, [isDesktop]);
  useAutoMuteOnScroll(sectionRef, !muted, fadeMute);

  if (!useCase) return <Navigate to="/" replace />;

  const heroPoster = useCase.videoPoster || useCase.image;
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    const active = isDesktop ? desktopMedia : mobileMedia;
    if (active.current) active.current.setMuted(next);
    if (!next) trackEvent('video_unmute', { resource: `Use case — ${useCase.title}` });
  };
  const openFullVideo = () => {
    if (!muted) fadeMute();
    setVideoAsset({ url: useCase.fullVideoUrl || useCase.videoUrl, title: useCase.title });
    trackEvent('video_watch_full', { resource: `Use case — ${useCase.title}` });
  };

  const Icon = useCase.icon;
  const canonicalUrl = `https://ctrl.aero/use-cases/${useCase.slug}`;
  const related = useCases.filter((u) => u.slug !== useCase.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${useCase.title} — Aviation Case Study | ctrl.aero`}
        description={useCase.tagline || useCase.description.slice(0, 180)}
        canonical={canonicalUrl}
        image={useCase.image}
        jsonLd={[
          articleJsonLd({
            headline: `${useCase.title} — ${useCase.caseStudy}`,
            description: useCase.description,
            image: useCase.image,
            url: canonicalUrl,
          }),
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://ctrl.aero/' },
            { name: 'Use Cases', url: 'https://ctrl.aero/use-cases' },
            { name: useCase.title, url: canonicalUrl },
          ]),
        ]}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      {/* Hero */}
      <section ref={sectionRef} className="relative isolate overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 pt-24 pb-12 sm:pt-28 sm:pb-16">
        {/* Desktop / tablet: media bleeds off the right and fades into the
            navy background — same pattern as the home hero. */}
        {useCase.videoUrl && (
          <div
            aria-hidden="true"
            className="pointer-events-none hidden lg:block absolute inset-y-0 right-0 lg:w-[70%] xl:w-[64%]"
            style={{
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 28%, rgba(0,0,0,0.85) 55%, #000 75%)',
              maskImage:
                'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 28%, rgba(0,0,0,0.85) 55%, #000 75%)',
            }}
          >
            <HeroMediaLayer
              ref={desktopMedia}
              posterImage={heroPoster}
              videoUrl={isDesktop ? useCase.videoUrl : null}
              variant="bleed"
              testid={`usecase-hero-video-${useCase.slug}`}
              objectPosition="right center"
              alt={`${useCase.title} field footage`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/60 via-blue-900/20 to-transparent" />
          </div>
        )}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/use-cases"
            className="inline-flex items-center text-white/80 hover:text-white text-sm mb-6 transition-colors"
            data-testid="usecase-back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All use cases
          </Link>

          <div className={`grid gap-8 ${useCase.videoUrl ? 'lg:grid-cols-12 items-center' : ''}`}>
            <div className={useCase.videoUrl ? 'lg:col-span-7' : ''}>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <div>
                  <p className="text-orange-400 text-xs sm:text-sm font-semibold uppercase tracking-wide mb-1">
                    Aviation Use Case • {useCase.caseStudy}
                  </p>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
                    {useCase.title}
                  </h1>
                </div>
              </div>

              <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-prose mt-6">
                {useCase.tagline || useCase.description}
              </p>

              {/* Mobile-only framed media card */}
              {useCase.videoUrl && (
                <div className="lg:hidden mt-8">
                  <HeroMediaLayer
                    ref={mobileMedia}
                    posterImage={heroPoster}
                    videoUrl={isDesktop ? null : useCase.videoUrl}
                    variant="card"
                    testid={`usecase-hero-video-mobile-${useCase.slug}`}
                    objectPosition="center"
                    className="aspect-video w-full"
                    alt={`${useCase.title} field footage`}
                  >
                    <div className="absolute top-3 left-3">
                      <HeroMuteToggle
                        muted={muted}
                        onToggle={toggleMute}
                        caption={useCase.videoCaption}
                        testid={`usecase-unmute-btn-mobile-${useCase.slug}`}
                        compact
                        align="left"
                        onWatchFull={openFullVideo}
                      />
                    </div>
                  </HeroMediaLayer>
                </div>
              )}
            </div>
            {/* Desktop spacer column so the copy stays left while the media
                bleeds behind — no explicit media element here since the layer
                above handles it. */}
            {useCase.videoUrl && (
              <div className="hidden lg:block lg:col-span-5 relative min-h-[380px]">
                <div className="absolute right-2 top-2">
                  <HeroMuteToggle
                    muted={muted}
                    onToggle={toggleMute}
                    caption={useCase.videoCaption}
                    testid={`usecase-unmute-btn-${useCase.slug}`}
                    onWatchFull={openFullVideo}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Image + Highlights */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-start">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-slate-50">
              <img src={useCase.image} alt={useCase.title} className="w-full h-auto object-cover" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1e2d3b] mb-4">
                What the UL101 delivered
              </h2>
              <ul className="space-y-3">
                {useCase.applications.map((app, i) => {
                  const isHighlight = typeof app === 'object' && app.highlight;
                  const text = typeof app === 'object' ? app.text : app;
                  return (
                    <li key={text} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 mt-1 text-blue-600 flex-shrink-0" />
                      <span className={isHighlight ? 'text-orange-600 font-bold text-lg' : 'text-slate-700 leading-relaxed'}>
                        {text}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 flex flex-wrap gap-3">
                {useCase.pdfs?.map((pdf, i) => (
                  <Button
                    key={i}
                    onClick={() => setGatedAsset({ title: pdf.title, url: pdf.url })}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg"
                    data-testid={`usecase-pdf-${i}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {pdf.label}
                  </Button>
                ))}
                {useCase.videoUrl && (
                  <Button
                    onClick={() => setVideoAsset({ url: useCase.fullVideoUrl || useCase.videoUrl, title: useCase.title })}
                    className="bg-[#1e2d3b] hover:bg-[#15202b] text-white shadow-lg"
                    data-testid="usecase-video-btn"
                  >
                    <Play className="w-4 h-4 mr-2" /> Watch the video
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Long-form crawlable body */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-slate max-w-none">
            {useCase.body.map((section, i) => (
              <div key={section.heading} className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e2d3b] mb-3">{section.heading}</h2>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">{section.text}</p>
              </div>
            ))}
          </article>

          <div className="mt-10 p-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl text-white text-center shadow-lg">
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Run this diagnosis on your fleet</h3>
            <p className="text-white/90 mb-4 text-sm sm:text-base">
              The CTRL UL101 Aviation Troubleshooter is the sensor that made this case study possible.
            </p>
            <div className="cta-group cta-group-sm max-w-xl mx-auto">
              <Link
                to="/products/ul101"
                className="bg-white text-blue-700 hover:bg-blue-50 rounded-lg text-sm font-semibold transition-colors"
                data-testid="usecase-product-link"
              >
                See the UL101 Troubleshooter →
              </Link>
              <Button
                onClick={() => onRequestDemo && onRequestDemo({ product: 'UL101' })}
                className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
                data-testid="usecase-demo-cta"
              >
                Request a demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related use cases — internal linking for crawlers */}
      <section className="py-12 sm:py-16 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1e2d3b] mb-8 text-center">
            More aviation use cases
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/use-cases/${r.slug}`}
                className="group bg-white rounded-2xl border border-slate-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 overflow-hidden"
                data-testid={`related-usecase-${r.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-[#1e2d3b] mb-1 group-hover:text-blue-600 transition-colors">{r.title}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{r.tagline || r.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <GatedDownloadModal
        open={Boolean(gatedAsset)}
        onOpenChange={(open) => { if (!open) setGatedAsset(null); }}
        resourceTitle={gatedAsset?.title}
        resourceUrl={gatedAsset?.url}
      />
      <VideoModal
        open={Boolean(videoAsset)}
        onOpenChange={(open) => { if (!open) setVideoAsset(null); }}
        videoUrl={videoAsset?.url}
        title={videoAsset?.title}
      />
    </div>
  );
};

export default UseCasePage;
